import { useEffect, useRef, useState, useCallback } from "react";
import { Camera, Check, LogOut, X, WifiOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { enqueueCapture, listPending, removeById, markRetry } from "../lib/photoshopDb";
import { captureFrame, readOrientation, watchOrientation } from "../lib/camera";
import { cn } from "../lib/utils";

// NZ rego: 2-6 alphanumeric. Matches backend `orphan_photos.normalise_rego`.
const REGO_RE = /^[A-Z0-9]{2,6}$/;

// Same guard as the driver camera: without it an impatient second tap banks
// two near-identical frames of the same panel.
const SHUTTER_LOCKOUT_MS = 900;

/**
 * PhotoShop — Feb 2026 yard workflow (phone-only screen).
 *
 * Dispatcher on a phone lands here after login. Enter a rego, tap to
 * capture photos (in-browser `getUserMedia`, so nothing leaks to the
 * device's photo library), and each capture uploads immediately as an
 * orphan photo tagged with the rego. Backend auto-attaches to any
 * matching non-terminal job — either now, or the moment a job appears.
 *
 * Offline queue: photos captured with no signal go to IndexedDB and
 * retry-upload every 15s while the tab is open (see `photoshopDb.js`).
 */
export default function PhotoShopPage() {
  const { logout } = useAuth();
  const [step, setStep] = useState("rego"); // "rego" | "camera" | "done"
  const [rego, setRego] = useState("");
  const [regoError, setRegoError] = useState("");
  const [captured, setCaptured] = useState([]); // [{id, thumbUrl}]
  const [serverIds, setServerIds] = useState({}); // local id -> server photo id
  const [recentCaptures, setRecentCaptures] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [orient, setOrient] = useState(readOrientation);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const lastShotAt = useRef(0);
  const drainingRef = useRef(false);
  const rerunRef = useRef(false);
  const inFlightRef = useRef(new Set());
  const tombstonesRef = useRef(new Set());

  useEffect(() => {
    const update = () => setOrient(readOrientation());
    update();
    return watchOrientation(update);
  }, []);

  // ─── recent-captures panel (server-side history) ─────────────────
  const refreshRecent = useCallback(async () => {
    try {
      const r = await api.get("/photos/orphan/recent");
      setRecentCaptures(r.data?.captures || []);
    } catch { /* silent */ }
  }, []);
  useEffect(() => {
    refreshRecent();
    const t = setInterval(refreshRecent, 30000);
    return () => clearInterval(t);
  }, [refreshRecent]);

  // ─── offline queue drain — retry every 15s while tab is open ─────
  //
  // Single-flight. The shutter, the 15s timer and the `online` event can
  // all call this at once, and without a guard each call listed the same
  // IndexedDB rows and POSTed them again: three shots on a slow link
  // produced five photos on the job, and the duplicates couldn't be
  // removed from the phone because only one server id was remembered per
  // photo. If a drain is asked for while one is running, we note it and
  // do exactly one more pass at the end.
  const drainQueue = useCallback(async () => {
    if (drainingRef.current) { rerunRef.current = true; return; }
    drainingRef.current = true;
    try {
      do {
        rerunRef.current = false;
        const pending = await listPending();
        setPendingCount(pending.length);
        if (!pending.length || !navigator.onLine) break;
        for (const row of pending) {
          if (inFlightRef.current.has(row.id)) continue;
          // Binned since this pass listed its rows. `pending` is a snapshot
          // holding the blob in memory, so without this check the upload
          // went ahead from that copy and the photo landed on the job with
          // no thumbnail left on the phone to delete it from.
          if (tombstonesRef.current.has(row.id)) {
            tombstonesRef.current.delete(row.id);
            await removeById(row.id);
            continue;
          }
          inFlightRef.current.add(row.id);
          try {
            const fd = new FormData();
            fd.append("rego", row.rego);
            fd.append("file", row.blob, `capture-${row.id}.jpg`);
            const res = await api.post("/photos/orphan", fd, {
              headers: { "Content-Type": "multipart/form-data" },
              // A yard with one bar will otherwise hang this request for
              // as long as the browser allows, stalling the queue behind it.
              timeout: 30000,
            });
            await removeById(row.id);
            const serverId = res.data?.id;
            if (serverId && tombstonesRef.current.has(row.id)) {
              // Binned while this upload was already in the air — the
              // thumbnail went but the photo still landed on the job.
              // Now we know its server id, take it straight back off.
              tombstonesRef.current.delete(row.id);
              await api.delete(`/photos/orphan/${serverId}`).catch(() => {});
            } else if (serverId) {
              // Remember what the server called it, so the X on a
              // thumbnail can delete the real photo, not just the copy.
              setServerIds((m) => ({ ...m, [row.id]: serverId }));
            }
          } catch (e) {
            await markRetry(row.id, e?.message);
            break; // don't hammer if one fails — likely all fail
          } finally {
            inFlightRef.current.delete(row.id);
          }
        }
        const after = await listPending();
        setPendingCount(after.length);
        if (after.length < pending.length) refreshRecent();
      } while (rerunRef.current);
    } finally {
      drainingRef.current = false;
    }
  }, [refreshRecent]);
  useEffect(() => {
    drainQueue();
    const t = setInterval(drainQueue, 15000);
    window.addEventListener("online", drainQueue);
    return () => {
      clearInterval(t);
      window.removeEventListener("online", drainQueue);
    };
  }, [drainQueue]);

  // ─── camera lifecycle ────────────────────────────────────────────
  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch {
      toast.error(
        "Camera access denied. Enable camera permission in your browser settings."
      );
    }
  }, []);
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);
  useEffect(() => {
    if (step === "camera") startCamera();
    return () => stopCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // ─── rego step submit ────────────────────────────────────────────
  const submitRego = () => {
    const cleaned = rego.trim().toUpperCase().replace(/\s|-/g, "");
    if (!REGO_RE.test(cleaned)) {
      setRegoError("Enter 2 to 6 letters/numbers (no spaces)");
      return;
    }
    setRego(cleaned);
    setRegoError("");
    setStep("camera");
  };

  // ─── capture button ──────────────────────────────────────────────
  //
  // Save first, upload after (Oct 2026). This used to upload inline and
  // only fall back to the queue if that threw — with no timeout on the
  // request, so on a weak signal in the yard the shutter sat disabled
  // behind a spinner until the connection gave up on its own. Same
  // complaint the drivers had before their camera was reworked. Now the
  // photo is on the phone before the button frees up, and the existing
  // 15s drain loop carries it to the server.
  const takePhoto = async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    if (Date.now() - lastShotAt.current < SHUTTER_LOCKOUT_MS) return;
    lastShotAt.current = Date.now();
    setUploading(true);
    try {
      const blob = await captureFrame(video);
      if (!blob) throw new Error("capture failed");
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      await enqueueCapture({ id, rego, blob, contentType: "image/jpeg" });
      setCaptured((c) => [...c, { id, thumbUrl: URL.createObjectURL(blob) }]);
      setPendingCount((n) => n + 1);
      drainQueue();
    } catch {
      toast.error("That photo didn't save. Try again.");
    } finally {
      setUploading(false);
    }
  };

  const finishSession = () => {
    stopCamera();
    setStep("done");
    setTimeout(() => {
      // Auto-return to rego entry so dispatcher can start on the next
      // vehicle without an extra tap.
      setCaptured((c) => {
        c.forEach((x) => x.thumbUrl && URL.revokeObjectURL(x.thumbUrl));
        return [];
      });
      setServerIds({});
      setRego("");
      setStep("rego");
    }, 2500);
  };

  // ─── binning a bad shot ──────────────────────────────────────────
  //
  // Oct 2026 — the X only ever filtered local state. By the time a
  // thumbnail exists the photo has uploaded and may already be attached
  // to the vehicle's job, so a dispatcher who binned a blurry shot
  // watched it disappear while it stayed on the job and on any claim.
  const removeCapture = async (id) => {
    const serverId = serverIds[id];
    try {
      if (serverId) {
        await api.delete(`/photos/orphan/${serverId}`);
      } else {
        // No server id yet, so it's either queued or mid-POST. Drop the
        // queued row AND leave a tombstone: a drain pass may already be
        // holding this photo in memory, in which case the tombstone is
        // what stops it — checked before the POST, and again when the
        // POST resolves so an upload already in the air gets deleted
        // server-side.
        tombstonesRef.current.add(id);
        await removeById(id);
        const p = await listPending();
        setPendingCount(p.length);
      }
      setCaptured((c) => {
        const gone = c.find((x) => x.id === id);
        if (gone?.thumbUrl) URL.revokeObjectURL(gone.thumbUrl);
        return c.filter((x) => x.id !== id);
      });
      setServerIds(({ [id]: _drop, ...rest }) => rest);
      refreshRecent();
    } catch {
      toast.error("Couldn't delete that photo — try again.");
    }
  };

  // ─── render ──────────────────────────────────────────────────────
  //
  // Feb 2026 mobile refit: root is `h-[100dvh]` (dynamic viewport
  // height — respects Android/iOS toolbar collapses) and camera step
  // uses a fixed-fill overlay layout so the video fills the ENTIRE
  // screen and controls float on top. Fixes: (1) shutter no longer
  // pushed off-screen by growing thumbnail strip, (2) landscape uses
  // the full viewport width for video with controls overlaying,
  // (3) safe-area padding stops iOS notch / Android navbar overlap.
  return (
    <div
      className="h-[100dvh] bg-slate-950 text-slate-100 flex flex-col overflow-hidden"
      style={{
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
        paddingLeft: "env(safe-area-inset-left)",
        paddingRight: "env(safe-area-inset-right)",
      }}
      data-testid="photoshop-page"
    >
      {/* Header — minimal on mobile. Email removed (not useful, took up
       * space + made logout button clip off-screen on narrow phones).
       * Hidden in the landscape viewfinder: 53px of chrome off a 390px-tall
       * screen pushed the shutter below the true centre, and the queue
       * count has the rego chip beside it anyway. */}
      {!(step === "camera" && orient.landscape) && (
        <header className="px-3 py-2 flex items-center justify-between border-b border-slate-800 shrink-0">
        <div className="flex items-center gap-1.5">
          <Camera className="h-5 w-5 text-emerald-400" />
          <span className="font-bold tracking-tight text-sm">PhotoShop</span>
        </div>
        <div className="flex items-center gap-3">
          {pendingCount > 0 && (
            <span className="flex items-center gap-1 text-xs text-amber-400" title="Queued for retry when online">
              <WifiOff className="h-3.5 w-3.5" /> {pendingCount}
            </span>
          )}
          <button
            onClick={logout}
            className="text-slate-400 hover:text-slate-200 p-2 -mr-1"
            data-testid="photoshop-logout-btn"
            aria-label="Log out"
          >
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </header>
      )}

      {/* Body */}
      <main className="flex-1 flex flex-col overflow-auto">
        {step === "rego" && (
          <RegoStep
            rego={rego}
            setRego={setRego}
            error={regoError}
            onSubmit={submitRego}
            recentCaptures={recentCaptures}
          />
        )}
        {step === "camera" && (
          <CameraStep
            videoRef={videoRef}
            rego={rego}
            captured={captured}
            uploading={uploading}
            orient={orient}
            pendingCount={pendingCount}
            onCapture={takePhoto}
            onRemove={removeCapture}
            onFinish={finishSession}
            onCancel={() => {
              stopCamera();
              // Photos already taken stay with the rego and still upload —
              // they belong to that vehicle either way. This only backs out
              // of the viewfinder.
              setCaptured((c) => {
                c.forEach((x) => x.thumbUrl && URL.revokeObjectURL(x.thumbUrl));
                return [];
              });
              setServerIds({});
              setStep("rego");
            }}
          />
        )}
        {step === "done" && <DoneStep rego={rego} count={captured.length} />}
      </main>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function RegoStep({ rego, setRego, error, onSubmit, recentCaptures }) {
  return (
    <div className="flex-1 flex flex-col justify-between px-6 py-8">
      <div className="flex-1 flex flex-col items-center justify-center max-w-md w-full mx-auto">
        <label className="text-xs uppercase tracking-widest text-slate-400 mb-3">
          Enter Rego
        </label>
        <input
          type="text"
          value={rego}
          onChange={(e) => setRego(e.target.value.toUpperCase())}
          onKeyDown={(e) => e.key === "Enter" && onSubmit()}
          maxLength={7}
          inputMode="text"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          autoFocus
          placeholder="KPD602"
          className={cn(
            "w-full bg-slate-900 border-2 rounded-md px-4 py-4 text-center",
            "text-3xl font-mono tracking-widest text-slate-100 placeholder-slate-600",
            "focus:outline-none",
            error ? "border-rose-500" : "border-slate-700 focus:border-emerald-500",
          )}
          data-testid="photoshop-rego-input"
        />
        {error && (
          <p className="text-rose-400 text-sm mt-2" data-testid="photoshop-rego-error">
            {error}
          </p>
        )}
        <button
          onClick={onSubmit}
          className="w-full mt-6 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-800 disabled:text-slate-500 text-white font-bold py-4 rounded-md text-lg tracking-wide"
          disabled={!rego}
          data-testid="photoshop-continue-btn"
        >
          Continue →
        </button>
      </div>

      {/* Recent captures panel — helps dispatcher confirm photos landed */}
      {recentCaptures.length > 0 && (
        <div className="max-w-md w-full mx-auto mt-6" data-testid="photoshop-recent-panel">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">
            Recent captures
          </div>
          <ul className="space-y-1.5">
            {recentCaptures.map((c) => (
              <li
                key={c.rego}
                className="flex items-center justify-between text-sm bg-slate-900 rounded-md px-3 py-2"
                data-testid={`photoshop-recent-${c.rego}`}
              >
                <span className="font-mono font-bold tracking-wider text-slate-200">
                  {c.rego}
                </span>
                <span className="text-xs text-slate-400">{c.count} photo{c.count !== 1 && "s"}</span>
                {c.attached_to_job_id ? (
                  <span className="flex items-center gap-1 text-xs text-emerald-400">
                    <Check className="h-3 w-3" /> attached
                  </span>
                ) : (
                  <span className="text-xs text-amber-400">waiting</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function CameraStep({ videoRef, rego, captured, uploading, orient, pendingCount, onCapture, onRemove, onFinish, onCancel }) {
  // Full-screen camera-app style: video fills the parent (which is
  // `flex-1` inside the `h-[100dvh]` root), controls float as overlays.
  //
  // Oct 2026 — turned sideways, a bottom-centre shutter is nowhere near
  // either thumb. In landscape the whole action row swings to the edge
  // where the phone's bottom (and the hand) now is, with the shutter
  // halfway up. Portrait is unchanged.
  const side = orient.landscape ? orient.side : null;
  return (
    <div className="flex-1 relative bg-black overflow-hidden">
      {/* Video preview fills entire area */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className="absolute inset-0 w-full h-full object-cover"
        data-testid="photoshop-video"
      />

      {/* Rego chip — opposite the controls strip, or it covers Done and the
          first thumbnail when the controls swing to the left edge. */}
      <div
        className={cn(
          "absolute top-3 z-10 bg-emerald-900/85 backdrop-blur-sm border border-emerald-500 rounded-full px-3 py-1.5 flex items-center gap-2 text-sm shadow-lg",
          side === "left" ? "right-3" : "left-3",
        )}
        data-testid="photoshop-rego-chip"
      >
        <span className="font-mono font-bold tracking-widest text-emerald-100">{rego}</span>
        <span className="text-xs text-emerald-200/80 border-l border-emerald-500/60 pl-2">
          {captured.length} photo{captured.length !== 1 && "s"}
        </span>
        {pendingCount > 0 && (
          <span
            className="flex items-center gap-1 text-xs text-amber-300 border-l border-emerald-500/60 pl-2"
            title="Queued — will send when there's signal"
            data-testid="photoshop-chip-pending"
          >
            <WifiOff className="h-3 w-3" /> {pendingCount}
          </span>
        )}
      </div>

      {/* Thumbnail strip — floats clear of the action row so it never
       * pushes the shutter about. Scrolls if more than fit. */}
      {captured.length > 0 && (
        <div
          className={cn(
            "absolute pointer-events-none",
            side === "right" && "top-3 bottom-3 right-28 w-16",
            side === "left" && "top-3 bottom-3 left-28 w-16",
            !side && "bottom-24 left-0 right-0 px-3",
          )}
          data-testid="photoshop-thumbs"
        >
          <div className={cn(
            "flex gap-2 pointer-events-auto",
            side ? "flex-col overflow-y-auto h-full" : "overflow-x-auto pb-1",
          )}>
            {captured.map((c) => (
              <div key={c.id} className="relative shrink-0">
                <img
                  src={c.thumbUrl}
                  alt=""
                  className="h-14 w-14 object-cover rounded-md border-2 border-white/80 shadow-lg"
                />
                <button
                  onClick={() => onRemove(c.id)}
                  className="absolute -top-1 -right-1 bg-rose-600 hover:bg-rose-500 text-white rounded-full p-0.5 shadow-md"
                  data-testid={`photoshop-remove-${c.id}`}
                  aria-label="Remove photo"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action row — pinned to the bottom when upright, to the thumb-side
       * edge when sideways, with a translucent backdrop so it stays
       * readable over any scene. */}
      <div
        className={cn(
          "absolute flex items-center justify-center",
          side === "right" && "inset-y-0 right-0 w-28 bg-gradient-to-l from-slate-950/90 via-slate-950/60 to-transparent",
          side === "left" && "inset-y-0 left-0 w-28 bg-gradient-to-r from-slate-950/90 via-slate-950/60 to-transparent",
          !side && "bottom-0 left-0 right-0 justify-between bg-gradient-to-t from-slate-950/90 via-slate-950/60 to-transparent px-4 pt-6 pb-4",
        )}
        data-testid="photoshop-controls"
        data-placement={side ? `side-${side}` : "bottom"}
      >
        <button
          onClick={onCancel}
          className={cn(
            "text-slate-100 bg-slate-800/70 hover:bg-slate-700/80 backdrop-blur-sm px-3 py-2 rounded-md text-sm font-medium",
            side && "absolute bottom-6",
          )}
          data-testid="photoshop-cancel-btn"
        >
          Back
        </button>
        <button
          onClick={onCapture}
          disabled={uploading}
          className="h-16 w-16 rounded-full bg-white hover:bg-slate-100 disabled:bg-slate-500 disabled:cursor-not-allowed active:scale-95 transition-transform border-4 border-slate-950 shadow-2xl flex items-center justify-center shrink-0"
          data-testid="photoshop-shutter-btn"
          aria-label="Capture photo"
        >
          {uploading ? (
            <Loader2 className="h-7 w-7 text-slate-700 animate-spin" />
          ) : (
            <div className="h-12 w-12 rounded-full bg-white border-2 border-slate-950/20" />
          )}
        </button>
        <button
          onClick={onFinish}
          disabled={captured.length === 0}
          className={cn(
            "text-slate-100 bg-emerald-600/90 hover:bg-emerald-500 disabled:bg-slate-800/50 disabled:text-slate-500 backdrop-blur-sm rounded-md px-4 py-2 text-sm font-medium",
            side && "absolute top-6",
          )}
          data-testid="photoshop-done-btn"
        >
          Done
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function DoneStep({ rego, count }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
      <div className="h-20 w-20 rounded-full bg-emerald-500/20 border-2 border-emerald-500 flex items-center justify-center mb-4">
        <Check className="h-10 w-10 text-emerald-400" />
      </div>
      <div className="text-xl font-bold mb-1" data-testid="photoshop-done-msg">
        {count} photo{count !== 1 && "s"} saved for {rego}
      </div>
      <p className="text-sm text-slate-400 max-w-xs">
        Will attach to the job as soon as it appears.
      </p>
    </div>
  );
}
